import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {Permissions} from 'auth/PermissionConstants';
import FileState from 'filestate/FileState';
import notifications from 'notifications';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import getTestTimestampProps from 'test/lib/cypress/getTestTimestampProps';
import {HasAvatar} from 'ui-library/avatar/Avatar';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import * as DateUtil from 'util/DateUtil';
import * as VersionHistoryUtil from 'util/VersionHistoryUtil';

interface ClonedFromInfo {
  name: string;
  id: string;
  versionId: number;
  modifiedAt: string;
}

export interface Version {
  id: number | string;
  modified_at?: string;
  modifiedAt?: string;
  created_at?: string;
  fetching?: boolean;
  name?: string;
  creator: HasAvatar;
  parentId: number | string | null;
}

interface HistoryPanelComponentState {
  showReverted: boolean;
}

export interface HistoryPanelComponentProps {
  openVersion: (versionId: string | number) => void;
  cloneFromVersion?: () => void;
  resetToVersion?: () => void;
  versions: Array<Version>;
  openVersionId: string | number;
  dirty: boolean;
  isLatest: boolean;
  isLockedForReview?: boolean;
  disableHistoryChanges?: boolean;
  isFirstTrackedVersion?: (version: Version) => boolean;
  unsaved: boolean;
  showNames?: boolean;
  hasParent: boolean;
  parent: ClonedFromInfo | null;
  permissions: Permissions;
  showSeconds: boolean;
}

const fileStates = require('FileStates').default.get();

function versionToTime(version: Version, showSeconds: boolean): string {
  const options = {showYear: true, showTime: true, showSeconds};
  const date = new Date((version.modified_at || version.modifiedAt)!);
  return DateUtil.readableTime(date, options);
}

class HistoryPanelComponent extends React.Component<HistoryPanelComponentProps, HistoryPanelComponentState> {
  static defaultProps: Partial<HistoryPanelComponentProps> = {
    dirty: false,
    showNames: false,
    showSeconds: false,
  };

  state: HistoryPanelComponentState = {
    showReverted: false,
  };

  tbodyRef = React.createRef<HTMLTableSectionElement>();

  componentDidUpdate(prevProps: HistoryPanelComponentProps, prevState: HistoryPanelComponentState): void {
    if (
      this.props.openVersionId !== prevProps.openVersionId ||
      this.state.showReverted !== prevState.showReverted ||
      !isEqual(this.props.versions, prevProps.versions)
    ) {
      const targetVersionId = _.findWhere(this.props.versions, {fetching: true}) || this.props.openVersionId;
      const nodeRef = this.refs[`version-${targetVersionId}`];
      if (nodeRef != null) {
        const node = ReactDOM.findDOMNode(nodeRef) as HTMLElement;
        const $tbody = $(ReactDOM.findDOMNode(this.tbodyRef.current)!);

        const height = $tbody.height();
        const top = $tbody.scrollTop();
        const bottom = top + height;

        const nodeHeight = $(node).height();
        const nodeTop = node.offsetTop;
        const nodeBottom = nodeTop + nodeHeight;

        if (!(top < node.offsetTop && nodeBottom < bottom)) {
          $tbody.stop().animate({scrollTop: node.offsetTop - nodeHeight / 2 - height / 2}, 200);
        }
      }
    }
  }

  toggleShowReverted = (showReverted: boolean): void => {
    this.setState({showReverted});
  };

  openVersion = (versionId: string): void => {
    if (this.props.openVersionId === versionId) {
      return;
    }
    if (this.props.unsaved || this.props.dirty) {
      notifications.warn('Switching versions is disabled while a save is in progress.', {
        id: 'HistoryPanelComponent.openVersion',
      });
      return;
    }
    this.props.openVersion(versionId);
  };

  openParent = (): void => {
    fileStates
      .openFolderItem(this.props.parent!.id, {
        open: true,
        source: 'history-panel-component',
        versionId: this.props.parent!.versionId,
      })
      .done((fileState: FileState) => {
        fileState.get('toolbar').trigger('openHistoryPanel');
      });
  };

  render(): JSX.Element {
    let version;
    const revertedInfo = VersionHistoryUtil.processVersions(this.props.versions);
    // eslint-disable-next-line no-restricted-syntax
    const hasReverts = _.any(_.values(revertedInfo));
    const isFetching = (version: Version): boolean =>
      version.fetching === undefined ? false : version.fetching;
    const hasFetching = _.any(this.props.versions, isFetching);

    const isActive = (version: Version): boolean => {
      // If one of the versions is fetching, we say that version is active.
      if (hasFetching) {
        return isFetching(version);
      }
      // If the versionId matches openVersionId it is active.
      // OR, we've just finished a save and have a new openVersionId, but we haven't yet
      // fetched the versions, we will say the last version is active.
      const lastVersion = this.props.versions[this.props.versions.length - 1];
      return this.props.openVersionId === version.id || (version === lastVersion && this.props.isLatest);
    };

    // Table layout if @props.showNames:
    //       | name    | user    |    time |
    // else  | user    |              time |
    const columnsClass = this.props.showNames ? 'third' : 'half';
    return (
      <Row
        className={classNames(
          css(
            this.props.resetToVersion == null && this.props.cloneFromVersion == null && styles.noMarginBottom
          ),
          'u-tiny-spacing-top'
        )}
        dataTestAttrs={{component: 'HistoryPanel', element: 'root'}}
      >
        <Col md={12}>
          {hasReverts ? (
            <div className="checkbox u-no-spacing-bottom">
              <label className={classNames({disabled: revertedInfo[this.props.openVersionId]})}>
                <input
                  type="checkbox"
                  checked={this.state.showReverted}
                  disabled={revertedInfo[this.props.openVersionId]}
                  onChange={this.toggleShowReverted.bind(null, !this.state.showReverted)}
                />
                Show full history
              </label>
            </div>
          ) : null}
          <Row>
            <Col md={12}>
              <table className="historyVersionTable table table-hover table-condensed scrollable">
                <thead className={css(styles.innerMenuPadding)}>
                  <tr>
                    {this.props.showNames ? (
                      <th className={classNames('name', columnsClass)}>
                        <div className="text-left">Name</div>
                      </th>
                    ) : undefined}
                    <th className={classNames('user', columnsClass)}>
                      <div className="text-left">User</div>
                    </th>
                    <th className={classNames('time', columnsClass)}>
                      <div className="text-right">Timestamp</div>
                    </th>
                  </tr>
                </thead>
                <tbody ref={this.tbodyRef} className={css(styles.innerMenuPadding)}>
                  {this.props.versions.length === 0 ? (
                    <tr className="version-row">
                      {this.props.showNames ? (
                        <td className={classNames('name', columnsClass)}>
                          <div />
                        </td>
                      ) : undefined}
                      <td className={classNames('user', columnsClass)}>
                        <div className="text-left">Loading...</div>
                      </td>
                      <td className={classNames('time', columnsClass)}>
                        <div />
                      </td>
                    </tr>
                  ) : (
                    (() => {
                      const result = [];
                      for (let i = this.props.versions.length - 1; i >= 0; i--) {
                        version = this.props.versions[i];
                        const isReverted = revertedInfo[version.id];
                        if (isReverted && !this.state.showReverted) {
                          continue;
                        }
                        const isFirstTrackedVersion =
                          this.props.isFirstTrackedVersion && this.props.isFirstTrackedVersion(version);
                        result.push(
                          <tr
                            key={`version-${version.id}`}
                            ref={`version-${version.id}`}
                            className={classNames(
                              css(isActive(version) ? styles.selectedVersionRow : styles.versionRow),
                              {
                                'version-row': true,
                                'is-reverted': isReverted,
                                'is-fetching': isFetching(version),
                                faded: isReverted,
                              }
                            )}
                            onClick={this.openVersion.bind(null, version.id)}
                            {...getTestProps({
                              component: 'HistoryPanel',
                              element: 'versionRow',
                              index: String(i),
                            })}
                          >
                            {this.props.showNames ? (
                              <td
                                className={classNames(css(styles.rowBorder), 'name', columnsClass)}
                                title={version.name}
                              >
                                <div className="text-left">{version.name}</div>
                              </td>
                            ) : undefined}
                            <td
                              className={classNames(css(styles.rowBorder), 'user', columnsClass)}
                              title={getPartyDisplayString(version.creator)}
                            >
                              <div
                                className="text-left"
                                {...getTestProps({component: 'HistoryPanel', element: 'creatorName'})}
                              >
                                {getPartyDisplayString(version.creator)}
                                {isFirstTrackedVersion && ' \u2022 First tracked version'}
                              </div>
                            </td>
                            <td
                              className={classNames(css(styles.rowBorder), 'time', columnsClass)}
                              title={versionToTime(version, this.props.showSeconds)}
                            >
                              <div
                                className="text-right"
                                {...getTestProps({component: 'HistoryPanel', element: 'timestamp'})}
                                {...getTestTimestampProps((version.modified_at || version.modifiedAt)!)}
                              >
                                {versionToTime(version, this.props.showSeconds)}
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      return result;
                    })()
                  )}
                </tbody>
              </table>
              {this.props.hasParent ? (
                <span className="pull-right u-small-spacing-top">
                  {'Cloned from '}
                  {this.props.parent != null ? (
                    <a onClick={this.openParent}>
                      {`${this.props.parent.name} (${DateUtil.readableTime(
                        new Date(this.props.parent.modifiedAt)
                      )})`}
                    </a>
                  ) : (
                    <strong>(Deleted)</strong>
                  )}
                </span>
              ) : null}
            </Col>
          </Row>
          {(this.props.resetToVersion != null || this.props.cloneFromVersion != null) && (
            <Row className="u-small-spacing-top">
              {this.props.resetToVersion != null ? (
                <Col sm={6} className={classNames({'col-sm-offset-3': this.props.cloneFromVersion == null})}>
                  <button
                    className="btn btn-af-primary btn-block"
                    onClick={this.props.resetToVersion}
                    // We disable resetting if they are viewing the current version or if a save is in progress.
                    // This also covers the case where if the entry has been lockedFromExternalChange and if the
                    // entry is dirty, that the user cannot attempt to reset the version (since unsaved will be
                    // true).
                    // We also disable resetting if entry is in review.
                    disabled={
                      this.props.isLatest ||
                      this.props.unsaved ||
                      this.props.isLockedForReview ||
                      this.props.disableHistoryChanges ||
                      this.props.dirty ||
                      !this.props.permissions.writable
                    }
                    type="button"
                    {...getTestProps({component: 'HistoryPanel', element: 'resetVersionButton'})}
                  >
                    Reset to version
                  </button>
                </Col>
              ) : undefined}
              {this.props.cloneFromVersion != null ? (
                <Col sm={6}>
                  <button
                    className={
                      this.props.resetToVersion != null
                        ? 'btn btn-af-default btn-block'
                        : 'btn btn-af-primary btn-block'
                    }
                    onClick={this.props.cloneFromVersion}
                    disabled={this.props.disableHistoryChanges || EntityStore.currentUser() == null}
                    type="button"
                  >
                    Clone from version
                  </button>
                </Col>
              ) : undefined}
            </Row>
          )}
        </Col>
      </Row>
    );
  }
}

export default HistoryPanelComponent;

const styles = StyleSheet.create({
  selectedVersionRow: {
    color: '#000DB5',
    backgroundColor: '#E8EDFF',
    fontWeight: 600,
  },
  versionRow: {
    paddingLeft: '8px',
  },
  innerMenuPadding: {
    paddingRight: 5,
  },
  rowBorder: {
    borderTop: 0,
  },
  noMarginBottom: {
    marginBottom: '-20px',
  },
});
